import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, GripVertical, Plus, Trash2, CheckCircle, Eye, EyeOff, X, Library, BookmarkPlus, Cloud } from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../../_shared/components';
import { Modal } from '../../_shared/components';
import {
  CONDITION_FIELDS, OPERATORS_BY_TYPE, SENDER_TOKENS, CHANNELS,
  conditionGroupSummary, calculateDistribution, calculateRowReach, calculateWillSend, evalRow, evalCondition, getMemberField,
  interpolateTemplate,
  type WaterfallRow, type Condition, type ConditionGroup, type SenderValue, type CellConfig,
  type Channel, type DistributionMap, type CopyEditorTarget,
} from './waterfallTypes';
import type { Campaign, CampaignMember, StatusMapping } from './types';
import { useSaveAsTemplate } from '../hooks/useSaveAsTemplate';
import { useTestDispatch } from '../hooks/useTestDispatch';

// ── Helpers ───────────────────────────────────────────────────────────────────

function matchedConditionLabels(m: CampaignMember, row: WaterfallRow): string[] {
  const allConditions = (row.condition_groups ?? []).flatMap(g => g.conditions);
  if (allConditions.length === 0 && row.conditions.length === 0) return [];
  const conditions = allConditions.length > 0 ? allConditions : row.conditions;
  return conditions
    .filter(c => evalCondition(m, c))
    .map(c => {
      const field = CONDITION_FIELDS.find(f => f.field === c.field);
      const label = field?.label ?? c.field;
      if (c.operator === 'is_not_null') return `${label} set`;
      if (c.operator === 'is_null') return `${label} empty`;
      if (field?.type === 'boolean' && c.operator === 'equals') return label;
      if (field?.type === 'boolean' && c.operator === 'not_equals') return `not ${label}`;
      if (c.operator === 'contains') return `${label}: "${c.value}"`;
      if (c.operator === 'in_last_n_days') return `${label} ≤ ${c.value}d`;
      if (c.operator === 'not_in_last_n_days') return `${label} > ${c.value}d`;
      if (c.operator === 'in_next_n_days') return `${label} within ${c.value}d`;
      if (c.operator === 'not_in_next_n_days') return `${label} not within ${c.value}d`;
      return `${label} ${c.operator} ${c.value}`;
    });
}

const inputCls = 'px-2.5 py-1.5 text-xs bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 rounded-lg text-nightshade-900 dark:text-nightshade-50 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 focus:border-transparent transition';
const selectCls = `${inputCls} cursor-pointer`;

function selectDiverseMembers(members: CampaignMember[], max: number): CampaignMember[] {
  if (members.length <= max) return members;
  const byStatus = new Map<string, CampaignMember[]>();
  for (const m of members) {
    const key = m.cm_status ?? '';
    const group = byStatus.get(key) ?? [];
    group.push(m);
    byStatus.set(key, group);
  }
  const result: CampaignMember[] = [];
  const groups = Array.from(byStatus.values());
  let round = 0;
  while (result.length < max) {
    let added = false;
    for (const group of groups) {
      if (result.length >= max) break;
      if (round < group.length) { result.push(group[round]); added = true; }
    }
    if (!added) break;
    round++;
  }
  return result;
}

function newRow(statusColumns: string[]): WaterfallRow {
  const cells: Record<string, CellConfig> = {};
  for (const col of statusColumns) {
    cells[col] = {
      enabled: true, channel: 'gong_engage', flow_id: '',
      has_custom_copy: false, subject: '', body: '', copy_status: null,
      sender: { type: 'token', token: 'gtme_owner_email' },
      cc: null, reply_to: null,
      has_claydr_handoff: false, handoff_hours: 8,
      email_type: 'manual',
    };
  }
  return {
    id: `row_${Date.now()}`,
    name: '',
    conditions: [],
    condition_logic: 'AND',
    condition_groups: [],
    group_logic: 'AND',
    follow_up_description: '',
    sender: null,
    cc: null,
    reply_to: null,
    cells,
  };
}

// ── Field search combobox ─────────────────────────────────────────────────────

function FieldSearch({ value, onChange }: { value: string; onChange: (f: string) => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const selected = CONDITION_FIELDS.find(f => f.field === value);
  const filtered = CONDITION_FIELDS.filter(f =>
    `${f.object} ${f.label}`.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative w-48">
      <input
        className={`${inputCls} w-full`}
        value={open ? query : (selected ? `${selected.object} · ${selected.label}` : '')}
        placeholder="Search fields…"
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); setQuery(''); }}
      />
      {open && (
        <div className="absolute z-30 top-full mt-1 left-0 w-56 bg-white dark:bg-nightshade-900 border border-nightshade-200 dark:border-nightshade-700 rounded-lg shadow-xl max-h-52 overflow-y-auto">
          {filtered.length === 0
            ? <p className="px-3 py-2 text-xs text-nightshade-400 italic">No fields match</p>
            : filtered.map(f => (
              <button
                key={f.field}
                onMouseDown={() => { onChange(f.field); setOpen(false); setQuery(''); }}
                className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                  value === f.field
                    ? 'bg-blueberry-50 dark:bg-blueberry-950/40 text-blueberry-700 dark:text-blueberry-300'
                    : 'text-nightshade-700 dark:text-nightshade-300 hover:bg-nightshade-50 dark:hover:bg-nightshade-800'
                }`}
              >
                <span className="text-nightshade-400 dark:text-nightshade-500">{f.object} · </span>{f.label}
              </button>
            ))
          }
        </div>
      )}
    </div>
  );
}

// ── Multi-value select (is any of / is not any of) ────────────────────────────

function MultiValueSelect({ field, members, value, onChange }: {
  field: string;
  members: CampaignMember[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Compute unique values + counts from members data for this field
  const valueCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const m of members) {
      const raw = getMemberField(m, field);
      const v = raw === null || raw === undefined ? '__null__' : String(raw);
      counts[v] = (counts[v] ?? 0) + 1;
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([val, count]) => ({ val, count, isNull: val === '__null__' }));
  }, [field, members]);

  const filtered = query
    ? valueCounts.filter(({ val, isNull }) =>
        isNull ? 'null'.includes(query.toLowerCase()) : val.toLowerCase().includes(query.toLowerCase())
      )
    : valueCounts;

  const toggle = (rawVal: string) => {
    const isNull = rawVal === '__null__';
    const _key = isNull ? '' : rawVal; // store empty string for null? Actually store the actual value
    // For null entries we store the literal string 'null' in the array so it round-trips
    const stored = isNull ? '__null__' : rawVal;
    const next = value.includes(stored) ? value.filter(v => v !== stored) : [...value, stored];
    onChange(next);
  };

  const label = value.length === 0
    ? 'Select values…'
    : `(${value.length}) ${value.slice(0, 2).map(v => v === '__null__' ? 'null' : v).join(', ')}${value.length > 2 ? '…' : ''}`;

  return (
    <div ref={ref} className="relative w-52">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`${inputCls} w-full flex items-center justify-between gap-1 text-left ${value.length === 0 ? 'text-nightshade-400' : ''}`}
      >
        <span className="truncate">{label}</span>
        <ChevronDown className="w-3 h-3 flex-shrink-0 text-nightshade-400" />
      </button>

      {open && (
        <div className="absolute z-30 top-full mt-1 left-0 w-64 bg-white dark:bg-nightshade-900 border border-nightshade-200 dark:border-nightshade-700 rounded-xl shadow-xl overflow-hidden">
          {/* Search */}
          <div className="px-3 py-2 border-b border-nightshade-100 dark:border-nightshade-800">
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full px-2 py-1 text-xs bg-nightshade-50 dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-700 rounded-lg text-nightshade-900 dark:text-nightshade-50 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 focus:border-transparent"
            />
          </div>
          {/* Options */}
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-3 text-xs text-nightshade-400 italic">No values</p>
            ) : filtered.map(({ val, count, isNull }) => {
              const stored = val;
              const checked = value.includes(stored);
              return (
                <button
                  key={val}
                  type="button"
                  onMouseDown={e => { e.preventDefault(); toggle(val); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-colors text-left hover:bg-nightshade-50 dark:hover:bg-nightshade-800 ${checked ? 'bg-blueberry-50 dark:bg-blueberry-950/30' : ''}`}
                >
                  <div className={`w-3.5 h-3.5 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${checked ? 'bg-blueberry-500 border-blueberry-500' : 'border-nightshade-300 dark:border-nightshade-600'}`}>
                    {checked && <span className="text-white text-[9px] leading-none">✓</span>}
                  </div>
                  <span className={`flex-1 truncate ${isNull ? 'italic text-nightshade-400' : 'text-nightshade-800 dark:text-nightshade-200'}`}>
                    {isNull ? 'null' : val}
                  </span>
                  <span className="text-nightshade-400 tabular-nums flex-shrink-0">{count}</span>
                </button>
              );
            })}
          </div>
          {/* Footer */}
          {value.length > 0 && (
            <div className="px-3 py-2 border-t border-nightshade-100 dark:border-nightshade-800 flex items-center justify-between">
              <span className="text-xs text-nightshade-500">{value.length} selected</span>
              <button
                type="button"
                onMouseDown={e => { e.preventDefault(); onChange([]); }}
                className="text-xs text-blueberry-500 hover:text-blueberry-700 transition-colors"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Contains-any chip input ───────────────────────────────────────────────────

function ContainsAnyInput({ fieldDef, value, onChange }: {
  field: string;
  fieldDef: { values?: string[] } | undefined;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const suggestions = useMemo(() => {
    const all = fieldDef?.values ?? [];
    const lower = input.toLowerCase();
    return all.filter(v => !value.includes(v) && (!lower || v.toLowerCase().includes(lower)));
  }, [fieldDef?.values, value, input]);

  const addChip = (v: string) => {
    const trimmed = v.trim();
    if (trimmed && !value.includes(trimmed)) onChange([...value, trimmed]);
    setInput('');
  };

  const removeChip = (v: string) => onChange(value.filter(x => x !== v));

  return (
    <div ref={ref} className="relative w-52">
      <div className={`${inputCls} w-full flex flex-wrap items-center gap-1 min-h-[30px] cursor-text`} onClick={() => setOpen(true)}>
        {value.map(v => (
          <span key={v} className="inline-flex items-center gap-0.5 bg-nightshade-100 dark:bg-nightshade-800 text-nightshade-600 dark:text-nightshade-400 rounded-full px-2 py-0.5 text-[10px]">
            {v}
            <button type="button" onClick={e => { e.stopPropagation(); removeChip(v); }} className="hover:text-pomegranate-500 transition-colors">
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[60px] bg-transparent outline-none text-xs text-nightshade-900 dark:text-nightshade-50 placeholder:text-nightshade-400"
          value={input}
          onChange={e => { setInput(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={e => {
            if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
              e.preventDefault();
              addChip(input);
            }
            if (e.key === 'Backspace' && !input && value.length > 0) {
              removeChip(value[value.length - 1]);
            }
          }}
          placeholder={value.length === 0 ? 'Type or select…' : ''}
        />
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-30 top-full mt-1 left-0 w-64 bg-white dark:bg-nightshade-900 border border-nightshade-200 dark:border-nightshade-700 rounded-xl shadow-xl overflow-hidden">
          <div className="max-h-56 overflow-y-auto">
            {suggestions.map(s => (
              <button
                key={s}
                type="button"
                onMouseDown={e => { e.preventDefault(); addChip(s); }}
                className="w-full text-left px-3 py-2 text-xs text-nightshade-800 dark:text-nightshade-200 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sender input ──────────────────────────────────────────────────────────────

function SenderInput({ value, onChange, label, optional }: {
  value: SenderValue | null;
  onChange: (v: SenderValue | null) => void;
  label: string;
  optional?: boolean;
}) {
  if (value === null && optional) {
    return (
      <button
        onClick={() => onChange({ type: 'token', token: 'gtme_owner_email' })}
        className="text-xs text-blueberry-500 hover:text-blueberry-700 transition-colors"
      >
        + Add {label.toLowerCase()}
      </button>
    );
  }

  // For required senders (non-optional), treat null as default token
  const resolved = value ?? { type: 'token' as const, token: 'gtme_owner_email' };

  return (
    <div className="flex items-center gap-1.5">
      <select
        className={`${selectCls} w-24`}
        value={resolved.type}
        onChange={e => {
          if (e.target.value === 'token') onChange({ type: 'token', token: 'gtme_owner_email' });
          else onChange({ type: 'static', email: '' });
        }}
      >
        <option value="token">Token</option>
        <option value="static">Email</option>
      </select>
      {resolved.type === 'token' ? (
        <select
          className={`${selectCls} flex-1`}
          value={resolved.token ?? 'gtme_owner_email'}
          onChange={e => onChange({ type: 'token', token: e.target.value as any })}
        >
          {SENDER_TOKENS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      ) : (
        <input type="email" className={`${inputCls} flex-1`} placeholder="email@clay.com" value={resolved.email ?? ''} onChange={e => onChange({ type: 'static', email: e.target.value })} />
      )}
      {optional && (
        <button onClick={() => onChange(null)} className="text-nightshade-400 hover:text-pomegranate-500 transition-colors flex-shrink-0" title="Remove">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

// ── Condition builder ─────────────────────────────────────────────────────────

/** Flat condition list builder — used by enrichment flags settings (single condition) */
export function ConditionBuilder({ conditions, logic, onChange, onLogicChange, members }: {
  conditions: Condition[];
  logic: 'AND' | 'OR';
  onChange: (c: Condition[]) => void;
  onLogicChange: (l: 'AND' | 'OR') => void;
  members: CampaignMember[];
}) {
  return (
    <ConditionListInner
      conditions={conditions}
      logic={logic}
      onChange={onChange}
      onLogicChange={onLogicChange}
      members={members}
    />
  );
}

/** Inner condition list (shared by ConditionBuilder and ConditionGroupBuilder) */
function ConditionListInner({ conditions, logic, onChange, onLogicChange, members }: {
  conditions: Condition[];
  logic: 'AND' | 'OR';
  onChange: (c: Condition[]) => void;
  onLogicChange: (l: 'AND' | 'OR') => void;
  members: CampaignMember[];
}) {
  const addCondition = () => onChange([...conditions, { field: 'sales_tier', operator: 'equals', value: 'Tier 1' }]);
  const removeCondition = (i: number) => onChange(conditions.filter((_, idx) => idx !== i));
  const updateCondition = (i: number, patch: Partial<Condition>) => {
    const next = [...conditions];
    const merged = { ...next[i], ...patch };
    const fieldDef = CONDITION_FIELDS.find(f => f.field === merged.field);
    const ops = fieldDef ? OPERATORS_BY_TYPE[fieldDef.type] : OPERATORS_BY_TYPE.text;
    if (!ops.find(o => o.value === merged.operator)) merged.operator = ops[0].value;
    if (['is_null', 'is_not_null'].includes(merged.operator)) merged.value = null;
    if (['in_any_of', 'not_in_any_of', 'contains_any'].includes(merged.operator) && !Array.isArray(merged.value)) merged.value = [];
    if (!['in_any_of', 'not_in_any_of', 'contains_any'].includes(merged.operator) && Array.isArray(merged.value)) merged.value = null;
    if ('field' in patch && ['in_any_of', 'not_in_any_of', 'contains_any'].includes(merged.operator)) merged.value = [];
    // Boolean fields: auto-set value to 'true'/'false' based on operator
    if (fieldDef?.type === 'boolean') {
      merged.value = merged.operator === 'not_equals' ? 'false' : 'true';
    }
    next[i] = merged;
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {conditions.map((cond, i) => {
        const fieldDef = CONDITION_FIELDS.find(f => f.field === cond.field);
        const ops = fieldDef ? OPERATORS_BY_TYPE[fieldDef.type] : OPERATORS_BY_TYPE.text;
        const isMultiSelect = ['in_any_of', 'not_in_any_of'].includes(cond.operator);
        const isContainsAny = cond.operator === 'contains_any';
        const needsValue = !['is_null', 'is_not_null'].includes(cond.operator) && fieldDef?.type !== 'boolean';
        return (
          <div key={i} className="flex items-center gap-2 flex-wrap">
            {i > 0 ? (
              <button
                onClick={() => onLogicChange(logic === 'AND' ? 'OR' : 'AND')}
                className="text-xs font-bold text-blueberry-500 hover:text-blueberry-700 w-8 text-center flex-shrink-0 transition-colors"
              >
                {logic}
              </button>
            ) : <span className="w-8 flex-shrink-0" />}

            <FieldSearch value={cond.field} onChange={field => updateCondition(i, { field })} />

            <select className={`${selectCls} w-32`} value={cond.operator} onChange={e => updateCondition(i, { operator: e.target.value as any })}>
              {ops.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>

            {needsValue && (
              isContainsAny ? (
                <ContainsAnyInput
                  field={cond.field}
                  fieldDef={fieldDef}
                  value={Array.isArray(cond.value) ? cond.value : []}
                  onChange={v => updateCondition(i, { value: v })}
                />
              ) : isMultiSelect ? (
                <MultiValueSelect
                  field={cond.field}
                  members={members}
                  value={Array.isArray(cond.value) ? cond.value : []}
                  onChange={v => updateCondition(i, { value: v })}
                />
              ) : ['in_last_n_days', 'not_in_last_n_days', 'in_next_n_days'].includes(cond.operator) ? (
                <input type="number" min="1" className={`${inputCls} w-20`}
                  value={typeof cond.value === 'string' ? cond.value : ''} placeholder="days"
                  onChange={e => updateCondition(i, { value: e.target.value })} />
              ) : fieldDef?.type === 'picklist' && fieldDef.values ? (
                <select className={`${selectCls} w-28`} value={typeof cond.value === 'string' ? cond.value : ''} onChange={e => updateCondition(i, { value: e.target.value })}>
                  {fieldDef.values.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              ) : (
                <input className={`${inputCls} w-28`} value={typeof cond.value === 'string' ? cond.value : ''} onChange={e => updateCondition(i, { value: e.target.value })} placeholder="value" />
              )
            )}

            <button onClick={() => removeCondition(i)} className="text-nightshade-300 hover:text-pomegranate-500 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
      <button onClick={addCondition} className="flex items-center gap-1 text-xs text-blueberry-500 hover:text-blueberry-700 transition-colors">
        <Plus className="w-3.5 h-3.5" /> Add condition
      </button>
    </div>
  );
}

/** Condition group builder — used by waterfall rows */
export function ConditionGroupBuilder({ groups, groupLogic, onGroupsChange, onGroupLogicChange, members }: {
  groups: ConditionGroup[];
  groupLogic: 'AND' | 'OR';
  onGroupsChange: (g: ConditionGroup[]) => void;
  onGroupLogicChange: (l: 'AND' | 'OR') => void;
  members: CampaignMember[];
}) {
  const addGroup = () => {
    onGroupsChange([...groups, { id: crypto.randomUUID(), conditions: [{ field: 'sales_tier', operator: 'equals', value: 'Tier 1' }], logic: 'AND' }]);
  };

  const removeGroup = (idx: number) => {
    onGroupsChange(groups.filter((_, i) => i !== idx));
  };

  const updateGroupConditions = (idx: number, conditions: Condition[]) => {
    const next = [...groups];
    next[idx] = { ...next[idx], conditions };
    onGroupsChange(next);
  };

  const updateGroupLogic = (idx: number, logic: 'AND' | 'OR') => {
    const next = [...groups];
    next[idx] = { ...next[idx], logic };
    onGroupsChange(next);
  };

  return (
    <div className="space-y-3">
      {groups.map((group, gi) => (
        <React.Fragment key={group.id}>
          {gi > 0 && (
            <div className="flex justify-center">
              <button
                onClick={() => onGroupLogicChange(groupLogic === 'AND' ? 'OR' : 'AND')}
                className="text-xs font-bold px-3 py-1 rounded-full bg-blueberry-50 dark:bg-blueberry-950/30 text-blueberry-600 dark:text-blueberry-400 border border-blueberry-200 dark:border-blueberry-800 hover:bg-blueberry-100 dark:hover:bg-blueberry-900/40 transition-colors"
              >
                {groupLogic}
              </button>
            </div>
          )}
          <div className="rounded-lg border border-nightshade-200 dark:border-nightshade-700 bg-white/50 dark:bg-nightshade-800/30 px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-mono font-medium text-nightshade-500 dark:text-nightshade-400 uppercase tracking-wider">
                Group {gi + 1}
              </span>
              {groups.length > 1 && (
                <button
                  onClick={() => removeGroup(gi)}
                  className="text-nightshade-300 hover:text-pomegranate-500 transition-colors"
                  title="Remove group"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <ConditionListInner
              conditions={group.conditions}
              logic={group.logic}
              onChange={c => updateGroupConditions(gi, c)}
              onLogicChange={l => updateGroupLogic(gi, l)}
              members={members}
            />
          </div>
        </React.Fragment>
      ))}
      <button onClick={addGroup} className="flex items-center gap-1 text-xs text-blueberry-500 hover:text-blueberry-700 transition-colors">
        <Plus className="w-3.5 h-3.5" /> Add group
      </button>
    </div>
  );
}

// ── Cell cards (per status column) ────────────────────────────────────────────

function CellCards({ cells, statusColumns, rowDist, onUpdate, onEditCopy }: {
  cells: Record<string, CellConfig>;
  statusColumns: string[];
  rowDist: Record<string, number>;
  onUpdate: (col: string, patch: Partial<CellConfig>) => void;
  onEditCopy: (col: string) => void;
}) {
  const [confirmRemoveCol, setConfirmRemoveCol] = useState<string | null>(null);
  const [flowIdError, setFlowIdError] = useState<string | null>(null);
  const [senderError, setSenderError] = useState<string | null>(null);
  const [copyRequiredError, setCopyRequiredError] = useState<string | null>(null);
  const [copyMergeTagError, setCopyMergeTagError] = useState<{ col: string; message: string } | null>(null);

  return (
    <div>
      <p className="text-[11px] font-mono font-medium text-nightshade-500 dark:text-nightshade-400 uppercase tracking-wider mb-2">Flow IDs &amp; Copy</p>
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(statusColumns.length, 3)}, minmax(0, 1fr))` }}>
        {statusColumns.map(col => {
          const cell = cells[col] ?? { enabled: true, channel: 'gong_engage', flow_id: '', has_custom_copy: false, subject: '', body: '', copy_status: null, has_claydr_handoff: false, handoff_hours: 8, email_type: 'manual' as const };
          const count = rowDist[col];
          const _isReady = cell.has_custom_copy && cell.copy_status === 'ready';
          const _isDraft = cell.has_custom_copy && cell.copy_status === 'draft';

          return (
            <div key={col} className={`rounded-2xl border p-3.5 space-y-3 transition-colors ${
              cell.enabled
                ? 'bg-white dark:bg-nightshade-900 border-nightshade-200 dark:border-nightshade-700'
                : 'bg-nightshade-50 dark:bg-nightshade-800/30 border-nightshade-100 dark:border-nightshade-800 opacity-60'
            }`}>
              {/* Card header */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => onUpdate(col, { enabled: !cell.enabled })}
                  className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${cell.enabled ? 'bg-blueberry-500 border-blueberry-500' : 'border-nightshade-300 dark:border-nightshade-600'}`}
                >
                  {cell.enabled && <span className="text-white text-xs leading-none">✓</span>}
                </button>
                <span className="text-sm font-semibold text-nightshade-800 dark:text-nightshade-100 mr-auto">{col}</span>

                {cell.enabled && (
                  <>
                    {/* Draft / Ready toggle — always available */}
                    <div className="flex rounded-full bg-nightshade-100 dark:bg-nightshade-800 p-0.5 text-xs">
                      <button
                        onClick={() => onUpdate(col, { copy_status: 'draft' })}
                        className={`px-2 py-0.5 transition-colors rounded-full ${
                          cell.copy_status !== 'ready'
                            ? 'bg-nightshade-900 dark:bg-white text-white dark:text-nightshade-900 font-medium'
                            : 'text-nightshade-500 dark:text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200'
                        }`}
                      >Draft</button>
                      <button
                        onClick={() => {
                          if (!cell.sender) { setSenderError(col); return; }
                          if (!cell.flow_id.trim()) { setFlowIdError(col); return; }
                          if ((cell.channel === 'slack_copilot' || cell.channel === 'inflection') && !cell.has_custom_copy) { setCopyRequiredError(col); return; }
                          // Merge tags required for Ready on all channels
                          {
                            const missing: string[] = [];
                            if (!cell.body?.includes('{{First Name}}')) missing.push('{{First Name}}');
                            if (!cell.body?.includes('{{Sender First Name}}')) missing.push('{{Sender First Name}}');
                            if (missing.length > 0) { setCopyMergeTagError({ col, message: `Body must include ${missing.join(' and ')}` }); return; }
                          }
                          setSenderError(null);
                          setFlowIdError(null);
                          setCopyRequiredError(null);
                          setCopyMergeTagError(null);
                          onUpdate(col, { copy_status: 'ready' });
                        }}
                        className={`px-2 py-0.5 transition-colors rounded-full ${
                          cell.copy_status === 'ready'
                            ? 'bg-nightshade-900 dark:bg-white text-white dark:text-nightshade-900 font-medium'
                            : 'text-nightshade-500 dark:text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200'
                        }`}
                      >Ready</button>
                    </div>

                    {/* Add / Edit copy */}
                    <button
                      onClick={() => { if (copyRequiredError === col) setCopyRequiredError(null); onEditCopy(col); }}
                      className={`text-xs transition-colors font-medium whitespace-nowrap ${copyRequiredError === col ? 'text-pomegranate-500 hover:text-pomegranate-700' : 'text-blueberry-500 hover:text-blueberry-700 dark:hover:text-blueberry-300'}`}
                    >
                      {cell.has_custom_copy ? 'Edit copy' : (cell.channel === 'slack_copilot' || cell.channel === 'inflection') ? 'Add copy (required)' : 'Add custom copy'}
                    </button>
                    {cell.has_custom_copy && (
                      <button
                        onClick={() => setConfirmRemoveCol(col)}
                        className="text-nightshade-300 hover:text-pomegranate-500 transition-colors"
                        title="Remove custom copy"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}

                    {/* ClayDR Handoff — only for gong_engage / inflection */}
                    {(cell.channel === 'gong_engage' || cell.channel === 'inflection') && (
                      cell.has_claydr_handoff ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-medium text-nightshade-600 dark:text-nightshade-300">ClayDR Handoff</span>
                          <button
                            onClick={() => onUpdate(col, { has_claydr_handoff: false, handoff_hours: 8 })}
                            className="text-nightshade-300 hover:text-pomegranate-500 transition-colors"
                            title="Remove handoff"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => onUpdate(col, { has_claydr_handoff: true, handoff_hours: 8 })}
                          className="text-xs text-blueberry-500 hover:text-blueberry-700 dark:hover:text-blueberry-300 font-medium whitespace-nowrap"
                        >
                          Add ClayDR Handoff
                        </button>
                      )
                    )}
                  </>
                )}

                {count !== undefined && (
                  <span className="text-sm font-semibold text-nightshade-600 dark:text-nightshade-300 tabular-nums">{count}</span>
                )}
              </div>

              {cell.enabled && (
                <>
                  {/* Channel */}
                  <div>
                    <p className="text-xs text-nightshade-400 dark:text-nightshade-500 mb-1">Channel</p>
                    <select
                      className={`${selectCls} w-full`}
                      value={cell.channel}
                      onChange={e => {
                        const ch = e.target.value as Channel;
                        if (ch === 'slack_copilot') onUpdate(col, { channel: ch, cc: null, reply_to: null, has_claydr_handoff: false, handoff_hours: 8 });
                        else if (ch !== 'inflection') onUpdate(col, { channel: ch, cc: null, reply_to: null });
                        else onUpdate(col, { channel: ch });
                      }}
                    >
                      {CHANNELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>

                  {/* Sender */}
                  <div>
                    <p className="text-xs text-nightshade-400 dark:text-nightshade-500 mb-1">Sender</p>
                    <SenderInput
                      value={cell.sender ?? null}
                      onChange={sender => { if (senderError === col) setSenderError(null); onUpdate(col, { sender }); }}
                      label="Sender"
                    />
                    {senderError === col && (
                      <p className="text-xs text-pomegranate-500 mt-1">Sender required before marking Ready.</p>
                    )}
                  </div>

                  {/* CC + Reply-to — Inflection only */}
                  {cell.channel === 'inflection' && (
                    <>
                      <div>
                        <p className="text-xs text-nightshade-400 dark:text-nightshade-500 mb-1">CC <span className="text-nightshade-300 dark:text-nightshade-600">(optional)</span></p>
                        <SenderInput value={cell.cc ?? null} onChange={cc => onUpdate(col, { cc })} label="CC" optional />
                      </div>
                      <div>
                        <p className="text-xs text-nightshade-400 dark:text-nightshade-500 mb-1">Reply-to <span className="text-nightshade-300 dark:text-nightshade-600">(optional)</span></p>
                        <SenderInput value={cell.reply_to ?? null} onChange={reply_to => onUpdate(col, { reply_to })} label="Reply-to" optional />
                      </div>
                    </>
                  )}

                  {/* ClayDR Handoff — Time to Handoff */}
                  {cell.has_claydr_handoff && (
                    <div>
                      <p className="text-xs text-nightshade-400 dark:text-nightshade-500 mb-1">Time to Handoff (hours)</p>
                      <input
                        type="number"
                        min={1}
                        max={24}
                        className={`${inputCls} w-full`}
                        value={cell.handoff_hours}
                        onChange={e => {
                          const v = Math.max(1, Math.min(24, parseInt(e.target.value) || 8));
                          onUpdate(col, { handoff_hours: v });
                        }}
                      />
                    </div>
                  )}

                  {/* Flow ID */}
                  <div>
                    <p className="text-xs text-nightshade-400 dark:text-nightshade-500 mb-1">Flow ID</p>
                    <input
                      className={`${inputCls} w-full font-mono ${flowIdError === col ? 'ring-2 ring-pomegranate-400 border-pomegranate-400' : ''}`}
                      placeholder="e.g. 1144420067685858387"
                      value={cell.flow_id}
                      onChange={e => { if (flowIdError === col) setFlowIdError(null); onUpdate(col, { flow_id: e.target.value }); }}
                    />
                    {flowIdError === col && (
                      <p className="text-xs text-pomegranate-500 mt-1">Flow ID required before marking Ready.</p>
                    )}
                  </div>
                  {copyRequiredError === col && (
                    <p className="text-xs text-pomegranate-500">Custom copy required for Terra Co-Pilot.</p>
                  )}
                  {copyMergeTagError?.col === col && (
                    <p className="text-xs text-pomegranate-500">{copyMergeTagError.message}</p>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {confirmRemoveCol && (
        <Modal
          open
          title="Remove custom copy?"
          onClose={() => setConfirmRemoveCol(null)}
          footer={
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirmRemoveCol(null)}>Cancel</Button>
              <Button variant="danger" size="sm" onClick={() => {
                onUpdate(confirmRemoveCol, { has_custom_copy: false, subject: '', body: '', copy_status: null });
                setConfirmRemoveCol(null);
              }}>Remove</Button>
            </div>
          }
        >
          <p className="text-sm text-nightshade-700 dark:text-nightshade-300">
            Remove custom copy for <strong>{confirmRemoveCol}</strong>? The subject and body will be lost.
          </p>
        </Modal>
      )}
    </div>
  );
}

// ── Preview panel ─────────────────────────────────────────────────────────────

function PreviewPanel({ row, allRows, allMembers, statusColumns, campaign }: {
  row: WaterfallRow;
  allRows: WaterfallRow[];
  allMembers: CampaignMember[];
  statusColumns: string[];
  campaign: Campaign | null;
}) {
  // Waterfall-aware: only show members who reach this row (not claimed by an earlier row)
  const matched = useMemo(() => {
    const rowIndex = allRows.findIndex(r => r.id === row.id);
    const priorRows = allRows.slice(0, rowIndex);
    return allMembers.filter(m => !priorRows.some(r => evalRow(m, r)) && evalRow(m, row));
  }, [allMembers, allRows, row]);

  // Columns that have custom copy enabled
  const copyColumns = useMemo(() =>
    statusColumns.filter(col => row.cells[col]?.has_custom_copy),
    [statusColumns, row.cells],
  );
  const [selectedCopyCol, setSelectedCopyCol] = useState<string | null>(null);
  const activeCol = selectedCopyCol ?? copyColumns[0] ?? null;
  const sampleMember = matched[0] ?? null;

  return (
    <div className="border-t border-nightshade-100 dark:border-nightshade-800 bg-blueberry-50/40 dark:bg-blueberry-950/10 px-5 py-3 space-y-3">
      <div>
        <p className="text-xs font-semibold text-nightshade-600 dark:text-nightshade-400 mb-2">
          Preview — <span className="text-blueberry-600 dark:text-blueberry-400">{matched.length} members</span> match this bucket
        </p>
        {matched.length === 0 ? (
          <p className="text-xs text-nightshade-400 italic">No members match the current conditions.</p>
        ) : (
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {matched.slice(0, 50).map(m => {
              const sfdcId = m.contact_id ?? m.lead_id ?? null;
              const sfdcType = m.contact_id ? 'Contact' : 'Lead';
              const sfdcUrl = sfdcId ? `https://clay.lightning.force.com/lightning/r/${sfdcType}/${sfdcId}/view` : null;
              const reasons = matchedConditionLabels(m, row);
              return (
                <div key={m.id} className="flex items-start gap-3 text-xs group">
                  <div className="flex items-center gap-1 w-36 min-w-0 flex-shrink-0">
                    <span className="font-medium text-nightshade-800 dark:text-nightshade-200 truncate">{m.contact_name ?? '—'}</span>
                    {sfdcUrl && (
                      <a
                        href={sfdcUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open in Salesforce"
                        className="flex-shrink-0 text-blueberry-600 hover:text-blueberry-700 dark:text-blueberry-400 dark:hover:text-blueberry-300 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <Cloud className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  <span className="text-nightshade-400 truncate w-28 flex-shrink-0">{m.account_name ?? '—'}</span>
                  <span className="text-nightshade-400 w-12 flex-shrink-0">{m.sales_tier ?? '—'}</span>
                  {reasons.length > 0 && (
                    <span
                      className="text-nightshade-300 dark:text-nightshade-600 truncate min-w-0"
                      title={reasons.join(' · ')}
                    >
                      {reasons[0]}{reasons.length > 1 && <span className="ml-1 text-nightshade-300 dark:text-nightshade-700">+{reasons.length - 1}</span>}
                    </span>
                  )}
                </div>
              );
            })}
            {matched.length > 50 && <p className="text-xs text-nightshade-400 italic">… and {matched.length - 50} more</p>}
          </div>
        )}
      </div>

      {copyColumns.length > 0 && sampleMember && activeCol && (() => {
        const cell = row.cells[activeCol];
        const sender = cell?.sender ?? row.sender ?? null;
        const subject = cell?.subject ? interpolateTemplate(cell.subject, sampleMember, campaign, sender) : null;
        const body = cell?.body ? interpolateTemplate(cell.body, sampleMember, campaign, sender) : null;
        return (
          <div className="border-t border-nightshade-100 dark:border-nightshade-700 pt-3">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-xs font-semibold text-nightshade-600 dark:text-nightshade-400">
                Copy preview — <span className="text-nightshade-400 font-normal">{sampleMember.contact_name ?? 'sample member'}</span>
              </p>
              {copyColumns.length > 1 && (
                <select
                  className="ml-auto text-xs bg-transparent border border-nightshade-200 dark:border-nightshade-700 rounded px-1.5 py-0.5 text-nightshade-600 dark:text-nightshade-400"
                  value={activeCol}
                  onChange={e => setSelectedCopyCol(e.target.value)}
                >
                  {copyColumns.map(col => <option key={col} value={col}>{col}</option>)}
                </select>
              )}
            </div>
            {subject && (
              <p className="text-xs text-nightshade-700 dark:text-nightshade-300 mb-1">
                <span className="text-nightshade-400 dark:text-nightshade-500">Subject: </span>{subject}
              </p>
            )}
            {body && (
              <pre className="text-xs text-nightshade-600 dark:text-nightshade-400 whitespace-pre-wrap font-sans max-h-32 overflow-y-auto leading-relaxed">{body}</pre>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ── Row card ──────────────────────────────────────────────────────────────────

interface RowCardProps {
  row: WaterfallRow;
  allRows: WaterfallRow[];
  statusColumns: string[];
  dist: DistributionMap;
  reachCount: number;
  willSendCount: number;
  isCatchAll: boolean;
  allMembers: CampaignMember[];
  campaign: Campaign | null;
  disabled?: boolean;
  isTestMode?: boolean;
  campaignId: string;
  statusMapping: StatusMapping;
  onChange: (patch: Partial<WaterfallRow>) => void;
  onDelete: () => void;
  onOpenEditor: (col: string) => void;
}

function RowCard({ row, allRows, statusColumns, dist, reachCount, willSendCount, isCatchAll, allMembers, campaign, disabled = false, isTestMode = false, campaignId, statusMapping, onChange, onDelete, onOpenEditor }: RowCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sendState, setSendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const testDispatchMutation = useTestDispatch();

  const testMembers = useMemo(() => {
    if (!isTestMode) return [];
    const rowIndex = allRows.findIndex(r => r.id === row.id);
    const priorRows = allRows.slice(0, rowIndex);
    const claimed = new Set(priorRows.flatMap(r => allMembers.filter(m => evalRow(m, r)).map(m => m.id)));
    const reaching = allMembers.filter(m => {
      if (claimed.has(m.id) || !evalRow(m, row)) return false;
      // Only include members whose cm_status is mapped to a column
      const rawStatus = m.cm_status ?? '';
      return Object.values(statusMapping).some(statuses =>
        statuses.some(s => s.toLowerCase() === rawStatus.toLowerCase())
      );
    });
    return selectDiverseMembers(reaching, 3);
  }, [isTestMode, allRows, allMembers, row, statusMapping]);

  const handleSendTest = async () => {
    if (testMembers.length === 0) return;
    setSendState('sending');
    try {
      await testDispatchMutation.mutateAsync({
        campaignId,
        row_name: row.name,
        member_ids: testMembers.map(m => m.id),
        row,
        status_mapping: statusMapping,
      });
      setSendState('sent');
    } catch {
      setSendState('error');
    }
  };

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: isCatchAll || disabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const rowDist = dist[row.id] ?? {};

  const totalCount = reachCount;

  const rowIsReady = useMemo(() => {
    const enabledCells = statusColumns.map(col =>
      row.cells[col] ?? { enabled: true, copy_status: null }
    ).filter(c => c.enabled);
    return enabledCells.length === 0 || enabledCells.every(c => c.copy_status === 'ready');
  }, [row.cells, statusColumns]);

  const defaultCell: CellConfig = { enabled: true, channel: 'gong_engage', flow_id: '', has_custom_copy: false, subject: '', body: '', copy_status: null, sender: { type: 'token', token: 'gtme_owner_email' }, cc: null, reply_to: null, has_claydr_handoff: false, handoff_hours: 8, email_type: 'manual' };
  const updateCell = (col: string, patch: Partial<CellConfig>) =>
    onChange({ cells: { ...row.cells, [col]: { ...(row.cells[col] ?? defaultCell), ...patch } } });

  return (
      <div ref={setNodeRef} style={style} className={`ring-1 rounded-2xl overflow-hidden transition-colors ${confirmDelete ? 'bg-pomegranate-50 dark:bg-pomegranate-950/20 ring-pomegranate-300 dark:ring-pomegranate-700' : 'bg-white dark:bg-nightshade-900 ring-nightshade-200 dark:ring-nightshade-700'}`}>
        {/* Collapsed header */}
        <div className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-nightshade-50 dark:hover:bg-nightshade-800/50 transition-colors" onClick={() => setExpanded(e => !e)}>
          {/* Drag handle */}
          <div className="flex-shrink-0" onClick={e => e.stopPropagation()}>
            {isCatchAll || disabled ? (
              <div className="w-4 h-4" />
            ) : (
              <button
                {...attributes}
                {...listeners}
                className="text-nightshade-300 hover:text-nightshade-500 dark:hover:text-nightshade-400 cursor-grab active:cursor-grabbing transition-colors p-0.5 touch-none"
                aria-label="Drag to reorder"
              >
                <GripVertical className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <span className="text-nightshade-300 dark:text-nightshade-600 flex-shrink-0">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </span>

          {/* Name */}
          <span className="text-sm font-semibold text-nightshade-900 dark:text-nightshade-50 w-28 flex-shrink-0 truncate">
            {row.name || <span className="italic text-nightshade-400 font-normal">Unnamed</span>}
          </span>

          {/* Condition summary */}
          <span className="text-xs text-nightshade-500 dark:text-nightshade-400 flex-1 truncate min-w-0">
            {conditionGroupSummary(row.condition_groups, row.group_logic)}
          </span>

          {/* Total count + sends + auto-computed status pill */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs text-nightshade-400 dark:text-nightshade-500 tabular-nums">{totalCount} in bucket</span>
            <span className="text-xs font-semibold text-nightshade-700 dark:text-nightshade-200 tabular-nums">{willSendCount} sends</span>
            <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium border ${
              rowIsReady
                ? 'bg-matcha-50 dark:bg-matcha-900/20 text-matcha-700 dark:text-matcha-400 border-matcha-200 dark:border-matcha-800'
                : 'bg-tangerine-50 dark:bg-tangerine-900/20 text-tangerine-700 dark:text-tangerine-400 border-tangerine-200 dark:border-tangerine-800'
            }`}>
              {rowIsReady ? 'Ready' : 'Draft'}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0 ml-1" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowPreview(p => !p)}
              title="Preview matching members"
              className={`p-1 rounded transition-colors ${showPreview ? 'text-blueberry-500' : 'text-nightshade-300 hover:text-nightshade-600 dark:hover:text-nightshade-300'}`}
            >
              {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
            {!isCatchAll && !disabled && (
              <button onClick={() => setConfirmDelete(true)} className="p-1 text-nightshade-300 hover:text-pomegranate-500 transition-colors rounded">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Preview panel */}
        {showPreview && <PreviewPanel row={row} allRows={allRows} allMembers={allMembers} statusColumns={statusColumns} campaign={campaign} />}

        {/* Inline delete confirmation strip */}
        {confirmDelete && (
          <div className="border-t border-pomegranate-200 dark:border-pomegranate-800 bg-pomegranate-50 dark:bg-pomegranate-950/30 px-4 py-2.5 flex items-center gap-3">
            <span className="text-xs text-pomegranate-700 dark:text-pomegranate-400 flex-1">
              Delete <strong>{row.name || 'this row'}</strong>? This cannot be undone.
            </span>
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-xs text-nightshade-500 dark:text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200 transition-colors px-2 py-1 rounded"
            >
              Cancel
            </button>
            <button
              onClick={() => { onDelete(); setConfirmDelete(false); }}
              className="text-xs font-semibold text-pomegranate-600 dark:text-pomegranate-400 hover:text-pomegranate-800 dark:hover:text-pomegranate-200 transition-colors px-2 py-1 rounded"
            >
              Delete
            </button>
          </div>
        )}

        {/* Expanded form */}
        {expanded && !confirmDelete && (
          <div className={`border-t border-nightshade-100 dark:border-nightshade-800 px-5 py-5 space-y-5 bg-nightshade-50/40 dark:bg-nightshade-800/20 ${disabled ? 'pointer-events-none opacity-60' : ''}`}>
            {/* Name + description */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-nightshade-600 dark:text-nightshade-400 mb-1">Bucket name</label>
                <input className={`${inputCls} w-full`} value={row.name} onChange={e => onChange({ name: e.target.value })} placeholder="e.g. Tier 1" />
              </div>
              <div>
                <label className="block text-xs font-medium text-nightshade-600 dark:text-nightshade-400 mb-1">Follow-up action</label>
                <input className={`${inputCls} w-full`} value={row.follow_up_description} onChange={e => onChange({ follow_up_description: e.target.value })} placeholder="e.g. Clay DR automated sequence" />
              </div>
            </div>

            {/* Conditions */}
            {!isCatchAll && (
              <div>
                <label className="block text-xs font-medium text-nightshade-600 dark:text-nightshade-400 mb-2">Conditions</label>
                <ConditionGroupBuilder
                  groups={row.condition_groups}
                  groupLogic={row.group_logic}
                  onGroupsChange={condition_groups => {
                    // Keep flat conditions in sync for backward compat
                    const flat = condition_groups.flatMap(g => g.conditions);
                    const logic = condition_groups.length === 1 ? condition_groups[0].logic : row.condition_logic;
                    onChange({ condition_groups, conditions: flat, condition_logic: logic });
                  }}
                  onGroupLogicChange={group_logic => onChange({ group_logic })}
                  members={allMembers}
                />
              </div>
            )}

            {/* Cell cards */}
            <CellCards
              cells={row.cells}
              statusColumns={statusColumns}
              rowDist={rowDist}
              onUpdate={updateCell}
              onEditCopy={onOpenEditor}
            />

            {/* Test panel */}
            {isTestMode && (
              <div className="rounded-2xl border border-blueberry-200 dark:border-blueberry-800 bg-blueberry-50/60 dark:bg-blueberry-950/20 px-4 py-3">
                <p className="text-xs font-semibold text-nightshade-600 dark:text-nightshade-400 mb-1">Test members (up to 3)</p>
                <p className="text-xs text-nightshade-400 dark:text-nightshade-500 mb-2">Sends real emails to the people listed below. Use this to verify routing and copy before launching. Test sends are tagged in the dispatch log.</p>
                {!rowIsReady ? (
                  <p className="text-xs text-nightshade-400 italic">Mark all cells Ready to enable testing.</p>
                ) : (
                  <>
                    {testMembers.length === 0 ? (
                      <p className="text-xs text-nightshade-400 italic mb-2">No members match this bucket.</p>
                    ) : (
                      <div className="space-y-1.5 mb-3">
                        {testMembers.map(m => (
                          <div key={m.id} className="flex items-center gap-3 text-xs">
                            <span className="font-medium text-nightshade-800 dark:text-nightshade-200 w-36 truncate">{m.contact_name ?? '—'}</span>
                            <span className="text-nightshade-400 w-40 truncate">{m.contact_email ?? '—'}</span>
                            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-blueberry-100 dark:bg-blueberry-900/40 text-blueberry-700 dark:text-blueberry-300 border border-blueberry-200 dark:border-blueberry-800">{m.cm_status ?? '—'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={testMembers.length === 0 || sendState === 'sending'}
                        loading={sendState === 'sending'}
                        onClick={() => { void handleSendTest(); }}
                      >
                        Send test
                      </Button>
                      {sendState === 'sent' && (
                        <span className="text-xs text-matcha-600 dark:text-matcha-400 font-medium">
                          ✓ Sent {testMembers.length} record{testMembers.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      {sendState === 'error' && (
                        <span className="text-xs text-pomegranate-500">Send failed — check webhook config.</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

  );
}

// ── Main WaterfallConfig ──────────────────────────────────────────────────────

interface WaterfallConfigProps {
  campaignType: string;
  statusColumns: string[];
  statusMapping: StatusMapping;
  members: CampaignMember[];
  campaign?: Campaign | null;
  onRowsChange?: (rows: WaterfallRow[]) => void;
  initialRows?: WaterfallRow[];
  onEditCopy?: (target: CopyEditorTarget) => void;
  onOpenTemplateSidebar?: () => void;
  campaignId: string;
  disabled?: boolean;
  isTestMode?: boolean;
  savedAt?: Date | null;
  onSaveNow?: () => Promise<void>;
  savePending?: boolean;
  saveError?: boolean;
}

export function WaterfallConfig({ campaignType, statusColumns, statusMapping, members, campaign = null, onRowsChange, initialRows, onEditCopy, onOpenTemplateSidebar, campaignId, disabled = false, isTestMode = false, savedAt = null, savePending = false, saveError = false }: WaterfallConfigProps) {
  const [rows, setRows] = useState<WaterfallRow[]>(initialRows ?? []);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateNameError, setTemplateNameError] = useState('');
  const saveTemplateMutation = useSaveAsTemplate();
  const templateNameRef = useRef<HTMLInputElement>(null);

  // Seed rows when initialRows arrives non-empty (useState only runs once)
  useEffect(() => {
    if (initialRows?.length) setRows(initialRows);
  }, [initialRows?.length]);

  // Notify parent whenever rows change — debounced to avoid re-render cascade on every keystroke
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => onRowsChange?.(rows), 300);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [rows, onRowsChange]);

  // Structural fingerprint — only changes when conditions, cell enabled, or row order change.
  // Text-only edits (name, subject, body, flow_id) don't affect distribution calculations.
  const structuralKey = useMemo(() => JSON.stringify(
    rows.map(r => ({
      id: r.id,
      conditions: r.conditions,
      condition_logic: r.condition_logic,
      condition_groups: r.condition_groups,
      group_logic: r.group_logic,
      cells: Object.fromEntries(
        Object.entries(r.cells).map(([col, c]) => [col, { enabled: c.enabled }]),
      ),
    })),
  ), [rows]);

  const dist = useMemo(
    () => calculateDistribution(members, rows, statusMapping),
    [members, structuralKey, statusMapping],
  );

  const rowReach = useMemo(
    () => calculateRowReach(members, rows, statusMapping),
    [members, structuralKey, statusMapping],
  );

  const willSend = useMemo(
    () => calculateWillSend(members, rows, statusMapping),
    [members, structuralKey, statusMapping],
  );

  // Precompute row → col → matched members (first-match-wins waterfall).
  // Reuses the same structural key so it only recalculates when conditions change.
  const rowMembersByCol = useMemo(() => {
    const result: Record<string, Record<string, CampaignMember[]>> = {};
    for (const row of rows) result[row.id] = {};
    for (const member of members) {
      const rawStatus = member.cm_status ?? '';
      let parentCol: string | null = null;
      for (const [col, statuses] of Object.entries(statusMapping)) {
        if (statuses.some(s => s.toLowerCase() === rawStatus.toLowerCase())) { parentCol = col; break; }
      }
      if (!parentCol) continue;
      const matchedRow = rows.find(r => evalRow(member, r));
      if (!matchedRow) continue;
      (result[matchedRow.id][parentCol] ??= []).push(member);
    }
    return result;
  }, [members, structuralKey, statusMapping]);

  const unmappedCount = useMemo(() => {
    if (!statusMapping || Object.keys(statusMapping).length === 0) return 0;
    return members.filter(m => {
      const rawStatus = m.cm_status ?? '';
      return !Object.values(statusMapping).some(statuses =>
        statuses.some(s => s.toLowerCase() === rawStatus.toLowerCase())
      );
    }).length;
  }, [members, statusMapping]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const updateRow = (id: string, patch: Partial<WaterfallRow>) =>
    setRows(rs => rs.map(r => r.id === id ? { ...r, ...patch } : r));

  const deleteRow = (id: string) => setRows(rs => rs.filter(r => r.id !== id));

  const handleDragEnd = (event: DragEndEvent) => {
    if (disabled) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRows(rs => {
      const catchAllId = rs.at(-1)?.id;
      // Don't allow moving the catch-all or moving anything to its position
      if (active.id === catchAllId) return rs;
      const oldIndex = rs.findIndex(r => r.id === active.id);
      const newIndex = rs.findIndex(r => r.id === over.id);
      // Don't allow dropping onto or past the catch-all slot
      if (newIndex === rs.length - 1) return rs;
      return arrayMove(rs, oldIndex, newIndex);
    });
  };

  const addRow = () => {
    if (disabled) return;
    setRows(rs => {
      const last = rs.at(-1);
      const body = rs.slice(0, -1);
      const nr = newRow(statusColumns);
      return [...body, nr, ...(last ? [last] : [])];
    });
  };


  const catchAllId = rows.at(-1)?.id;
  const allCopyReady = rows.every(r =>
    Object.values(r.cells).every(c => !c.enabled || c.copy_status === 'ready')
  );

  const openSaveTemplate = () => {
    setTemplateName('');
    setTemplateNameError('');
    setSavingTemplate(true);
    setTimeout(() => templateNameRef.current?.focus(), 50);
  };

  const confirmSaveTemplate = async () => {
    const name = templateName.trim();
    if (!name) return;
    setTemplateNameError('');
    try {
      await saveTemplateMutation.mutateAsync({ name, campaign_type: campaignType, rows });
      setSavingTemplate(false);
      setTemplateName('');
    } catch (err: any) {
      if (err?.message === 'duplicate_template_name') {
        setTemplateNameError('Name already in use');
      } else {
        setTemplateNameError(err?.message ? `Save failed: ${err.message}` : 'Save failed');
      }
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-nightshade-900 dark:text-nightshade-100">Waterfall configuration</h2>
          <p className="text-xs text-nightshade-500 dark:text-nightshade-400 mt-0.5">Members evaluated top-to-bottom — first match wins.</p>
        </div>
        <div className="flex items-center gap-2">
          {allCopyReady && rows.length > 0 && (
            <span className="flex items-center gap-1 text-xs text-matcha-600 dark:text-matcha-400 font-medium">
              <CheckCircle className="w-3.5 h-3.5" /> All copy ready
            </span>
          )}
          {savedAt && !savePending && (
            <span className="text-xs text-nightshade-400">Saved {savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          )}
          {onOpenTemplateSidebar && !disabled && (
            <Button variant="neutral" size="sm" onClick={onOpenTemplateSidebar}>
              <Library className="w-3.5 h-3.5" /> Load template
            </Button>
          )}
          {rows.length > 0 && !disabled && (
            <Button variant="neutral" size="sm" onClick={openSaveTemplate}>
              <BookmarkPlus className="w-3.5 h-3.5" /> Save as template
            </Button>
          )}
          {!disabled && (
            <Button variant="neutral" size="sm" onClick={addRow}>
              <Plus className="w-3.5 h-3.5" /> Add row
            </Button>
          )}
          {savePending && (
            <span className="text-xs text-nightshade-400">Saving…</span>
          )}
        </div>
      </div>
      {saveError && (
        <p className="text-xs text-pomegranate-500 mb-3">Save failed — try again.</p>
      )}

      {/* Save as template inline name input */}
      {savingTemplate && (
        <div className="mb-4">
          <div className="flex items-center gap-2 p-3 rounded-2xl bg-blueberry-50 dark:bg-blueberry-950/20 border border-blueberry-200 dark:border-blueberry-800">
            <BookmarkPlus className="w-3.5 h-3.5 text-blueberry-500 flex-shrink-0" />
            <input
              ref={templateNameRef}
              value={templateName}
              onChange={e => { setTemplateName(e.target.value); if (templateNameError) setTemplateNameError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') void confirmSaveTemplate(); if (e.key === 'Escape') setSavingTemplate(false); }}
              placeholder="Template name…"
              className={`flex-1 px-2 py-1 text-xs bg-white dark:bg-nightshade-800 border rounded-lg text-nightshade-900 dark:text-nightshade-50 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 focus:border-transparent transition ${templateNameError ? 'border-pomegranate-400 dark:border-pomegranate-600' : 'border-blueberry-200 dark:border-blueberry-700'}`}
            />
            <Button variant="ghost" size="sm" onClick={() => setSavingTemplate(false)}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              loading={saveTemplateMutation.isPending}
              disabled={!templateName.trim()}
              onClick={() => { void confirmSaveTemplate(); }}
            >
              Save
            </Button>
          </div>
          {templateNameError && (
            <p className="text-xs text-pomegranate-500 mt-1 pl-3">{templateNameError}</p>
          )}
        </div>
      )}

      {/* Pinned "Not Mapped" excluded row */}
      {unmappedCount > 0 && (
        <div className="ring-1 ring-pomegranate-200 dark:ring-pomegranate-800 rounded-2xl bg-pomegranate-50/60 dark:bg-pomegranate-950/20 overflow-hidden mb-2">
          <div className="flex items-center gap-2 px-4 py-3">
            <div className="w-4 h-4 flex-shrink-0" />
            <span className="text-nightshade-300 dark:text-nightshade-600 flex-shrink-0">
              <ChevronRight className="w-4 h-4" />
            </span>
            <span className="text-sm font-semibold text-pomegranate-700 dark:text-pomegranate-400 w-28 flex-shrink-0">
              Not mapped
            </span>
            <span className="text-xs text-pomegranate-500 dark:text-pomegranate-500 flex-1">
              excluded — map statuses in Bucket mapping above
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs font-semibold text-pomegranate-500 dark:text-pomegranate-400 tabular-nums">{unmappedCount}</span>
              <span className="text-xs px-2.5 py-0.5 rounded-full font-medium border bg-pomegranate-100 dark:bg-pomegranate-900/30 text-pomegranate-700 dark:text-pomegranate-400 border-pomegranate-200 dark:border-pomegranate-800">
                Excluded
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Rows */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={rows.map(r => r.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {rows.map(row => (
              <RowCard
                key={row.id}
                row={row}
                allRows={rows}
                statusColumns={statusColumns}
                dist={dist}
                reachCount={rowReach[row.id] ?? 0}
                willSendCount={willSend[row.id] ?? 0}
                isCatchAll={row.id === catchAllId}
                allMembers={members}
                campaign={campaign}
                disabled={disabled}
                isTestMode={isTestMode}
                campaignId={campaignId}
                statusMapping={statusMapping}
                onChange={patch => updateRow(row.id, patch)}
                onDelete={() => deleteRow(row.id)}
                onOpenEditor={col => onEditCopy?.({
                  bucketName: row.name,
                  statusColumn: col,
                  subject: row.cells[col]?.subject ?? '',
                  body: row.cells[col]?.body ?? '',
                  flowId: row.cells[col]?.flow_id ?? '',
                  channel: row.cells[col]?.channel ?? 'gong_engage',
                  copyStatus: row.cells[col]?.copy_status ?? null,
                  emailType: row.cells[col]?.email_type ?? 'manual',
                  sampleMember: (rowMembersByCol[row.id]?.[col] ?? [])[0] ?? null,
                  campaign,
                  sender: row.cells[col]?.sender ?? row.sender ?? null,
                  matchedMembers: rowMembersByCol[row.id]?.[col] ?? [],
                  onSave: (subject, body, copy_status, channel, email_type) => {
                    const rowId = row.id;
                    setRows(rs => rs.map(r => r.id !== rowId ? r : {
                      ...r,
                      cells: { ...r.cells, [col]: { ...(r.cells[col] ?? { enabled: true, channel: 'gong_engage', flow_id: '', has_custom_copy: false }), subject, body, copy_status, channel, has_custom_copy: true, email_type } },
                    }));
                  },
                })}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
